import { reactive } from 'vue'
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import type { IFactoryRequest, IFactoryResponse } from '@bc-library/interface'
import { stringifyQuery } from 'vue-router'
import rfdc from 'rfdc';
const clone = rfdc();


export default (axios: AxiosInstance) => <T, D>({ method, query = {}, url, data, immediate = true, headers = undefined, responseType = 'json', afterReceivingHook = undefined, beforeSendingHook = undefined }: IFactoryRequest<D>) => {
  const request = reactive<IFactoryResponse<T, D>>({
    loading: immediate,
    data: undefined,
    loadingError: null,
    promise: Promise.resolve(undefined as T),
    refresh: (() => Promise.resolve(undefined as T)) as IFactoryResponse<T, D>['refresh']
  })

  const refresh: IFactoryResponse<T, D>['refresh'] = async ({ query, data, headers }) => {
    let urlWithQuery = url
    if (query)
      urlWithQuery = url.split('?')[0].length > 0 ? `${url.split('?')[0]}?${stringifyQuery(query)}` : `${url}?${stringifyQuery(query)}`
    
    if (request)
      request.loading = true

    const dataToSend = (beforeSendingHook && data) ? beforeSendingHook(clone(data)) : data
    
    // The last refresh sent owns the state: an earlier one answering after it must not overwrite its data or loading
    const isLatest = () => request.promise === newPromise

    // This promise chain handles everything
    const newPromise = axios({
      method,
      url: urlWithQuery,
      data: dataToSend,
      headers,
      responseType,
    }).then((response: AxiosResponse) => {
      // EXECUTION (ONCE): Transform data immediately
      const finalData = afterReceivingHook ? afterReceivingHook(response.data) : response.data

      // Update State
      if (isLatest())
        request.data = finalData

      // Return transformed data (so await receives it)
      return finalData
    }).catch((err: AxiosError) => {
      if (isLatest())
        request.loadingError = err
      return Promise.reject(err)
    }).finally(() => {
      if (isLatest())
        request.loading = false
    })

    // Assign the SAME promise to the object
    request.promise = newPromise
    

    // Return the SAME promise from the function
    return newPromise
  }

  request.refresh = refresh

  if (immediate)
    refresh({ query, data })
  
  return request
}
